import React, { useState, useEffect, useCallback } from 'react';
import { X, Loader2, BarChart2, Layers, Shield, AlertTriangle, Database, FileWarning, CheckCircle2, XCircle, Clock, Plus, ExternalLink, PenLine, Bot, User, Flag, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axios';
import AttributeResultPage from '../attribute-result/AttributeResultPage';
import { calendarDateParts } from '../../utils/calendarDate';
import SignOffPanel, { LockedRunBanner } from './SignOffPanel';
import { TENANT_PATHS } from '../../routing/paths.ts';
import CreateIssueModal from '../issues/CreateIssueModal';
import { isControlUuid } from '../../features/controls/functional.ts';
import { useAuditYear } from '../../contexts/AuditYearContext.tsx';

const TABS = [
  { id: 'summary',    label: 'Summary Results', Icon: BarChart2 },
  { id: 'population', label: 'Population C/A',  Icon: Database },
  { id: 'samples',    label: 'Samples Testing', Icon: Layers },
  { id: 'issues',     label: 'Issues',          Icon: FileWarning },
  { id: 'signoff',    label: 'Sign Off',        Icon: Shield },
];

export default function AttributeTestResultModal({ runId, onClose }) {
  const navigate = useNavigate();
  const { selectedAuditYearId } = useAuditYear();
  const [tab, setTab] = useState('summary');
  const [run, setRun] = useState(null);
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Exceptions fetched from the dedicated API (source of truth for Issues tab)
  const [exceptions, setExceptions] = useState([]);
  const [exceptionsLoading, setExceptionsLoading] = useState(false);
  const [showAddException, setShowAddException] = useState(false);

  // Issues linked to this control (fetched on load)
  const [linkedIssues, setLinkedIssues] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const loadExceptions = useCallback(async () => {
    if (!runId) return;
    setExceptionsLoading(true);
    try {
      const res = await axiosInstance.get(`/api/control-test-runs/${runId}/exceptions`);
      setExceptions(res.data?.exceptions || []);
    } catch {
      // non-fatal; fall back to empty
    } finally {
      setExceptionsLoading(false);
    }
  }, [runId]);

  const loadRun = async () => {
    if (!runId) return;
    setLoading(true);
    try {
      const issuesParams = selectedAuditYearId ? { audit_year_id: selectedAuditYearId } : {};
      const [runResp, issuesResp] = await Promise.allSettled([
        axiosInstance.get(`/api/control-test-runs/${runId}`),
        axiosInstance.get('/api/sox-issues', { params: issuesParams }),
      ]);
      if (runResp.status === 'fulfilled') {
        const runData = runResp.value.data?.run || runResp.value.data;
        setRun(runData);
        setAttributes(runResp.value.data?.attributes || []);
        const controlId = runData?.sox_control_id;
        if (controlId && isControlUuid(controlId) && issuesResp.status === 'fulfilled') {
          const all = issuesResp.value.data?.issues || [];
          const runNumber = runResp.value.data?.run?.run_number ?? runResp.value.data?.run_number;
          setLinkedIssues(
            all
              .filter(i => {
                const linkedToControl = (i.controls || []).some(c => c.sox_control_id === controlId);
                if (!linkedToControl) return false;
                const linkedRuns = i.overview_data?.linked_test_runs || [];
                return linkedRuns.some(r => r.run_number === runNumber);
              })
              .map(i => ({ id: i.id, issue_id: i.issue_id }))
          );
        }
      } else {
        setRun(null);
      }
    } finally {
      setLoading(false);
    }
  };

  // Silent in-place refresh after sign-off events — avoids unmounting AttributeResultPage.
  const refreshRunData = useCallback(async () => {
    if (!runId) return;
    try {
      const runResp = await axiosInstance.get(`/api/control-test-runs/${runId}`);
      const runData = runResp.data?.run || runResp.data;
      if (runData) {
        setRun(runData);
        setAttributes(runResp.data?.attributes || []);
      }
    } catch { /* non-fatal */ }
  }, [runId]);

  useEffect(() => { loadRun(); }, [runId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === 'issues') loadExceptions(); }, [tab, loadExceptions]);

  // Open exceptions count drives the Issues tab badge (from the dedicated exceptions API when loaded,
  // otherwise falls back to a count derived from attribute sample results).
  const openExceptions = exceptions.length > 0
    ? exceptions.filter(e => !e.false_positive && e.disposition === 'open').length
    : attributes.reduce((acc, attr) => {
        const samples = attr.sample_results || [];
        return acc + samples.filter(s => s.status === 'exception' && (!s.disposition || s.disposition === 'open')).length;
      }, 0);

  // allIssues is kept for the CreateIssueModal prefill (summary of deficiency text).
  const allIssues = exceptions.length > 0
    ? exceptions.map(ex => ({
        attribute_ref:   ex.attribute_ref || '—',
        attribute_label: ex.attribute_label || '',
        sample_ref:      ex.record_ref || '—',
        description:     ex.description || '—',
      }))
    : (() => {
        const items = [];
        attributes.forEach(attr => {
          (attr.sample_results || []).forEach(sr => {
            if (sr.status === 'exception') {
              items.push({ attribute_ref: attr.attribute_ref, attribute_label: attr.label || '', sample_ref: sr.record_ref || '—', description: sr.observation || '—' });
            }
          });
        });
        return items;
      })();

  const isLocked = run?.workflow_status === 'signed_off';
  const runLabel = run?.test_run_name || (run?.run_number ? `RUN-${run.run_number}` : runId ? `RUN-${runId}` : '');

  // Build locked context for CreateIssueModal
  const derivePeriod = (r) => {
    if (!r?.testing_period_start) return '';
    const p = calendarDateParts(r.testing_period_start);
    if (!p) return '';
    return `Q${Math.ceil(p.month / 3)} ${p.year}`;
  };

  const buildSummary = (issues) => {
    const groups = {};
    issues.forEach(issue => {
      if (!groups[issue.attribute_ref]) {
        groups[issue.attribute_ref] = { ref: issue.attribute_ref, label: issue.attribute_label, refs: [], descriptions: [] };
      }
      groups[issue.attribute_ref].refs.push(issue.sample_ref);
      if (issue.description && issue.description !== '—') groups[issue.attribute_ref].descriptions.push(issue.description.trim());
    });
    return Object.values(groups).map(g => {
      const header = `${g.ref}${g.label && g.label !== g.ref ? ' – ' + g.label : ''}: ${g.refs.length} exception${g.refs.length !== 1 ? 's' : ''} (${g.refs.join(', ')}).`;
      return g.descriptions.length > 0 ? `${header} ${g.descriptions.join(' ')}` : header;
    }).join('\n\n');
  };

  const lockedControl = run?.sox_control_id
    ? { id: run.sox_control_id, control_id: run.control_id || '', title: run.control_title || '' }
    : null;
  const lockedTestRun = run
    ? { run_number: run.run_number, test_run_id: run.id, overall_status: run.execution?.overall_status, total_exceptions: allIssues.length }
    : null;
  const prefillForm = {
    title: [run?.control_id, 'Control Deficiency', derivePeriod(run)].filter(Boolean).join(' – '),
    period: derivePeriod(run),
    summary_of_deficiency: buildSummary(allIssues),
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white">
      {/* Header */}
      <div className="shrink-0 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between px-6 py-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Test Results — {runLabel}</h2>
            <p className="text-xs text-gray-500">Review results, inspect samples, then sign off</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-200 rounded">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex items-center gap-1 px-6 pb-0">
          {TABS.map(({ id, label, Icon }) => {
            const active = tab === id;
            const showBadge = id === 'issues' && openExceptions > 0;
            const showLock  = id === 'signoff' && isLocked;
            const popSkipped = id === 'population' && run?.population_status === 'not_required';
            return (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  active
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                {popSkipped ? 'Population' : label}
                {popSkipped && (
                  <span
                    className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs ${
                      run?.population_skip_reason ? 'bg-gray-100 text-gray-600' : 'bg-amber-100 text-amber-700'
                    }`}
                    title={run?.population_skip_reason ? 'Population not required' : 'Population skipped — add a reason'}
                  >
                    Skipped
                  </span>
                )}
                {showBadge && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">
                    <AlertTriangle className="h-3 w-3" />{openExceptions}
                  </span>
                )}
                {showLock && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
                    <Lock className="h-3 w-3" />Signed Off
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading results…
          </div>
        ) : (
          <>
            {tab === 'summary' && (
              <div className="p-6 space-y-4">
                {isLocked && <LockedRunBanner />}
                <AttributeResultPage embeddedRunId={runId} onSignOff={refreshRunData} />
              </div>
            )}

            {tab === 'population' && (
              <div className="p-6">
                {isLocked && <LockedRunBanner />}
                <AttributeResultPage embeddedRunId={runId} onSignOff={refreshRunData} sectionFilter="population" />
              </div>
            )}

            {tab === 'samples' && (
              <div className="p-6 space-y-4">
                {isLocked && <LockedRunBanner />}
                <AttributeResultPage
                  embeddedRunId={runId}
                  onSignOff={refreshRunData}
                  sectionFilter="samples"
                />
                {openExceptions === 0 && (
                  <div className="flex items-center justify-end pt-2">
                    <button
                      onClick={() => setTab('signoff')}
                      className="px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700"
                    >
                      Proceed to Sign Off →
                    </button>
                  </div>
                )}
              </div>
            )}

            {tab === 'issues' && (
              <div className="p-6 space-y-4 max-w-4xl">
                {isLocked && <LockedRunBanner />}
                {/* Header */}
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Issues &amp; Exceptions</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Document root cause, remediation, and disposition for each exception before sign-off.</p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${
                      openExceptions === 0 ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'
                    }`}>
                      <FileWarning className="h-3.5 w-3.5" />
                      {openExceptions === 0 ? 'No open exceptions' : `${openExceptions} open`}
                    </span>
                    {linkedIssues.length > 0 ? (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {linkedIssues.slice(0, 2).map(issue => (
                          <button key={issue.id} onClick={() => navigate(TENANT_PATHS.ISSUE_DETAIL.replace(':id', issue.id))}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold bg-indigo-50 border border-indigo-200 text-indigo-700 rounded-full hover:bg-indigo-100 whitespace-nowrap">
                            <ExternalLink className="h-3 w-3" />{issue.issue_id}
                          </button>
                        ))}
                        {linkedIssues.length > 2 && <span className="text-[10px] text-gray-500">+{linkedIssues.length - 2} more</span>}
                      </div>
                    ) : !isLocked && exceptions.filter(e => !e.false_positive).length > 0 ? (
                      <button onClick={() => setShowCreateModal(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                        <Plus className="h-3.5 w-3.5" />Log Issue
                      </button>
                    ) : null}
                    {!isLocked && (
                      <button onClick={() => setShowAddException(s => !s)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50">
                        <Plus className="h-3.5 w-3.5" />Add Manual Exception
                      </button>
                    )}
                  </div>
                </div>

                {/* Add Manual Exception inline form */}
                {showAddException && !isLocked && (
                  <AddExceptionForm
                    runId={runId}
                    attributes={attributes}
                    onCreated={(exc) => { setExceptions(prev => [...prev, exc]); setShowAddException(false); }}
                    onCancel={() => setShowAddException(false)}
                  />
                )}

                {/* Exception cards */}
                {exceptionsLoading ? (
                  <div className="flex items-center justify-center py-10 text-gray-400">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />Loading exceptions…
                  </div>
                ) : exceptions.length === 0 ? (
                  <div className="text-center py-12 border border-dashed border-green-200 rounded-xl bg-green-50/40">
                    <CheckCircle2 className="h-8 w-8 mx-auto text-green-400 mb-2" />
                    <p className="text-sm font-medium text-green-700">No exceptions noted</p>
                    <p className="text-xs text-green-600 mt-1">All attributes were achieved without deficiency for all sampled items.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {exceptions.map((exc, idx) => (
                      <ExceptionEditCard
                        key={exc.id}
                        exc={exc}
                        exNum={idx + 1}
                        runId={runId}
                        isLocked={isLocked}
                        onUpdated={(updated) => setExceptions(prev => prev.map(e => e.id === updated.id ? updated : e))}
                        onDeleted={(id) => setExceptions(prev => prev.filter(e => e.id !== id))}
                      />
                    ))}
                  </div>
                )}

                {openExceptions > 0 && (
                  <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                    <p className="text-xs text-amber-700 flex items-center gap-1.5">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      {openExceptions} open exception{openExceptions !== 1 ? 's' : ''} must be addressed before sign-off.
                    </p>
                    <button onClick={() => setTab('signoff')}
                      className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
                      Go to Sign Off →
                    </button>
                  </div>
                )}
              </div>
            )}

            {tab === 'signoff' && (
              <div className="p-6 max-w-2xl">
                <SignOffPanel run={run} onSigned={loadRun} />
              </div>
            )}
          </>
        )}
      </div>

      {/* ── CreateIssueModal ─────────────────────────────────────────────────── */}
      {showCreateModal && (
        <CreateIssueModal
          onClose={() => setShowCreateModal(false)}
          onCreated={(created) => {
            setLinkedIssues(prev => [...prev, { id: created.id, issue_id: created.issue_id }]);
            setShowCreateModal(false);
          }}
          controls={[]}
          tenant={null}
          selectedAuditYearId={selectedAuditYearId}
          navigateOnCreate={false}
          lockedControl={lockedControl}
          lockedTestRun={lockedTestRun}
          prefillForm={prefillForm}
        />
      )}
    </div>
  );
}

// ─── Disposition helpers ──────────────────────────────────────────────────────

const DISP_META = {
  open:          { label: 'Open',          cls: 'bg-red-50 border-red-200 text-red-700',     Icon: XCircle },
  remediated:    { label: 'Remediated',    cls: 'bg-green-50 border-green-200 text-green-700', Icon: CheckCircle2 },
  accepted_risk: { label: 'Accepted Risk', cls: 'bg-amber-50 border-amber-200 text-amber-700', Icon: Clock },
};

const SEV_META = {
  minor:     'bg-yellow-50 border-yellow-200 text-yellow-700',
  major:     'bg-orange-50 border-orange-200 text-orange-700',
  pervasive: 'bg-red-50 border-red-200 text-red-700',
};

// ─── ExceptionEditCard ────────────────────────────────────────────────────────

function ExceptionEditCard({ exc, exNum, runId, isLocked, onUpdated, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    description:          exc.description || '',
    disposition:          exc.disposition || 'open',
    severity:             exc.severity || '',
    root_cause:           exc.root_cause || '',
    remediation:          exc.remediation || '',
    owner:                exc.owner || '',
    management_response:  exc.management_response || '',
    false_positive:       exc.false_positive || false,
    false_positive_reason: exc.false_positive_reason || '',
  });

  const disp = DISP_META[form.disposition] || DISP_META.open;
  const DispIcon = disp.Icon;

  const handleSave = async () => {
    if (form.false_positive && !form.false_positive_reason.trim()) {
      toast.error('A reason is required when marking as false positive.');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.severity) delete payload.severity;
      const res = await axiosInstance.patch(`/api/control-test-runs/${runId}/exceptions/${exc.id}`, payload);
      onUpdated(res.data);
      setOpen(false);
      toast.success('Exception saved.');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this exception? This cannot be undone.')) return;
    try {
      await axiosInstance.delete(`/api/control-test-runs/${runId}/exceptions/${exc.id}`);
      onDeleted(exc.id);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Delete failed.');
    }
  };

  const isFP = exc.false_positive;

  return (
    <div className={`border rounded-xl bg-white overflow-hidden ${isFP ? 'border-gray-200 opacity-60' : 'border-gray-200'}`}>
      {/* Summary row */}
      <div className="flex items-center gap-3 px-4 py-3 flex-wrap">
        <span className="text-[10px] font-bold text-gray-400 font-mono border border-gray-200 px-1.5 py-0.5 bg-gray-50">
          EX-{String(exNum).padStart(2, '0')}
        </span>

        {/* Source badge */}
        {exc.source === 'manual'
          ? <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-purple-700 bg-purple-50 border border-purple-200 px-1.5 py-0.5 rounded"><User className="h-3 w-3"/>Manual</span>
          : <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded"><Bot className="h-3 w-3"/>AI</span>
        }

        {/* Severity */}
        {exc.severity && (
          <span className={`text-[10px] font-semibold border px-1.5 py-0.5 rounded uppercase ${SEV_META[exc.severity] || 'bg-gray-50 border-gray-200 text-gray-600'}`}>
            {exc.severity}
          </span>
        )}

        {/* Attribute + record ref */}
        {exc.attribute_ref && (
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-slate-700 text-[10px] font-bold border border-slate-200">
            {exc.attribute_ref}
          </span>
        )}
        <span className="text-xs font-semibold text-gray-800 font-mono">{exc.record_ref}</span>

        {/* Disposition badge */}
        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold border px-2 py-0.5 rounded-full ${disp.cls}`}>
          <DispIcon className="h-3 w-3"/>{disp.label}
        </span>

        {isFP && (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-gray-500 bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded">
            <Flag className="h-3 w-3"/>False positive
          </span>
        )}

        {!isLocked && (
          <button onClick={() => setOpen(o => !o)}
            className="ml-auto text-[10px] text-indigo-500 hover:text-indigo-700 flex items-center gap-1">
            <PenLine className="h-3 w-3"/>{open ? 'Collapse' : 'Edit'}
          </button>
        )}
      </div>

      {/* Description preview (always visible) */}
      {exc.description && (
        <div className="px-4 pb-3 text-xs text-gray-600 leading-relaxed border-t border-gray-50">
          {exc.description}
        </div>
      )}

      {/* Inline edit form */}
      {open && (
        <div className="border-t border-indigo-100 bg-indigo-50/30 px-4 py-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            {/* Disposition */}
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Disposition</label>
              <select value={form.disposition} onChange={e => setForm(f => ({ ...f, disposition: e.target.value }))}
                className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 bg-white">
                <option value="open">Open</option>
                <option value="remediated">Remediated</option>
                <option value="accepted_risk">Accepted Risk</option>
              </select>
            </div>
            {/* Severity */}
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Severity</label>
              <select value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))}
                className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 bg-white">
                <option value="">— Not set —</option>
                <option value="minor">Minor</option>
                <option value="major">Major</option>
                <option value="pervasive">Pervasive</option>
              </select>
            </div>
          </div>

          {[
            { key: 'description',         label: 'Nature of Exception',  rows: 2 },
            { key: 'root_cause',          label: 'Root Cause',           rows: 2 },
            { key: 'remediation',         label: 'Remediation',          rows: 2 },
            { key: 'owner',               label: 'Owner',                rows: 1 },
            { key: 'management_response', label: 'Management Response',  rows: 2 },
          ].map(({ key, label, rows }) => (
            <div key={key}>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">{label}</label>
              <textarea rows={rows} value={form[key]}
                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                className="w-full text-xs border border-gray-300 rounded px-2.5 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-indigo-400"
                placeholder={label}
              />
            </div>
          ))}

          {/* False positive toggle */}
          <div className="border border-gray-200 rounded-lg px-3 py-2.5 bg-white space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.false_positive}
                onChange={e => setForm(f => ({ ...f, false_positive: e.target.checked, false_positive_reason: e.target.checked ? f.false_positive_reason : '' }))}
                className="rounded border-gray-300 text-indigo-600"
              />
              <span className="text-xs text-gray-700 font-medium">Dismiss as false positive</span>
            </label>
            {form.false_positive && (
              <textarea rows={2} value={form.false_positive_reason}
                onChange={e => setForm(f => ({ ...f, false_positive_reason: e.target.value }))}
                className="w-full text-xs border border-red-200 rounded px-2.5 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-red-300 bg-red-50/30"
                placeholder="Required: explain why this exception is a false positive"
              />
            )}
          </div>

          <div className="flex items-center justify-between pt-1">
            {exc.source === 'manual' ? (
              <button onClick={handleDelete} className="text-[10px] text-red-500 hover:text-red-700">Delete</button>
            ) : <span />}
            <div className="flex gap-2">
              <button onClick={() => setOpen(false)} className="px-3 py-1.5 text-xs text-gray-500 border border-gray-200 rounded hover:bg-gray-50">Cancel</button>
              <button onClick={handleSave} disabled={saving}
                className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1">
                {saving && <Loader2 className="h-3 w-3 animate-spin"/>}Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── AddExceptionForm ─────────────────────────────────────────────────────────

function AddExceptionForm({ runId, attributes, onCreated, onCancel }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    record_ref:     '',
    attribute_id:   '',
    description:    '',
    severity:       '',
    root_cause:     '',
    remediation:    '',
    owner:          '',
    disposition:    'open',
  });

  const handleSubmit = async () => {
    if (!form.record_ref.trim() || !form.attribute_id) {
      toast.error('Record ref and attribute are required.');
      return;
    }
    setSaving(true);
    try {
      const attr = attributes.find(a => a.attribute_id === form.attribute_id || a.id === form.attribute_id);
      const payload = {
        ...form,
        attribute_ref:   attr?.attribute_ref || '',
        attribute_label: attr?.label || '',
      };
      if (!payload.severity) delete payload.severity;
      const res = await axiosInstance.post(`/api/control-test-runs/${runId}/exceptions`, payload);
      onCreated(res.data);
      toast.success('Exception added.');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to add exception.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-indigo-200 rounded-xl bg-indigo-50/30 px-4 py-4 space-y-3">
      <p className="text-xs font-semibold text-indigo-700">Add Manual Exception</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Record Ref <span className="text-red-500">*</span></label>
          <input value={form.record_ref} onChange={e => setForm(f => ({ ...f, record_ref: e.target.value }))}
            className="w-full text-xs border border-gray-300 rounded px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-400"
            placeholder="e.g. INV-2026-0091"
          />
        </div>
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Attribute <span className="text-red-500">*</span></label>
          <select value={form.attribute_id} onChange={e => setForm(f => ({ ...f, attribute_id: e.target.value }))}
            className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 bg-white">
            <option value="">— Select —</option>
            {attributes.map(a => (
              <option key={a.attribute_id || a.id} value={a.attribute_id || a.id}>
                {a.attribute_ref} — {a.label || a.attribute_ref}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Description</label>
        <textarea rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          className="w-full text-xs border border-gray-300 rounded px-2.5 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-indigo-400"
          placeholder="Describe the exception"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Severity</label>
          <select value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))}
            className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 bg-white">
            <option value="">— Not set —</option>
            <option value="minor">Minor</option>
            <option value="major">Major</option>
            <option value="pervasive">Pervasive</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Disposition</label>
          <select value={form.disposition} onChange={e => setForm(f => ({ ...f, disposition: e.target.value }))}
            className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 bg-white">
            <option value="open">Open</option>
            <option value="remediated">Remediated</option>
            <option value="accepted_risk">Accepted Risk</option>
          </select>
        </div>
      </div>
      {[
        { key: 'root_cause',  label: 'Root Cause' },
        { key: 'remediation', label: 'Remediation' },
        { key: 'owner',       label: 'Owner' },
      ].map(({ key, label }) => (
        <div key={key}>
          <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">{label}</label>
          <textarea rows={key === 'owner' ? 1 : 2} value={form[key]}
            onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
            className="w-full text-xs border border-gray-300 rounded px-2.5 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-indigo-400"
            placeholder={label}
          />
        </div>
      ))}
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel} className="px-3 py-1.5 text-xs text-gray-500 border border-gray-200 rounded hover:bg-gray-50">Cancel</button>
        <button onClick={handleSubmit} disabled={saving}
          className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1">
          {saving && <Loader2 className="h-3 w-3 animate-spin"/>}Add Exception
        </button>
      </div>
    </div>
  );
}
